import { T } from '@transifex/react';
import { VStack } from '@vidahealth/vida-ui';
import { Image, View } from 'react-native';

import { CtaButton } from '#modules/ui/CtaButton';
import { Screen } from '#modules/ui/Screen';
import { ScreenHeading } from '#modules/ui/ScreenHeading';

// Fixed rather than derived from the image's intrinsic size (contrast
// ProgramHeroImage in the host app, which reads it via `Image.getSize`): this
// component has to stay independently mountable by a host with no onboarding
// rules engine (see the module's packaging test), and a fixed ratio is simpler
// than plumbing an async size read through a server-authored URL of unknown
// shape. Paired with `resizeMode='cover'` below, so design's asset (any ratio
// close to 3:2) fills the box edge-to-edge instead of letterboxing.
const IMAGE_ASPECT_RATIO = 3 / 2;
const IMAGE_MAX_HEIGHT = 320;

type InfoGateScreenProps = {
  /** The gate node's own page title — rendered as the screen's heading. */
  title: string | null;
  /** The gate node's own page description — rendered as the heading's subtitle. */
  description: string | null;
  /**
   * Optional artwork URL (the wire's `info_image`, camelized). `null` or
   * `undefined` both render the screen with no image slot — the server sends
   * an explicit `null` for "no image", but treating the two the same keeps
   * this component tolerant of an older/stubbed payload that omits the field.
   */
  imageUrl?: string | null;
  /**
   * Fires when the member presses Continue. The caller (DynamicAssessmentFlow)
   * wires this to the hook's `continueGate`, which sends the gate's own ack —
   * this component has no idea what that ack looks like, only that Continue
   * doesn't advance until it's pressed.
   */
  onContinue: () => void;
  isSubmitting?: boolean;
  /** Per-flow testID prefix, e.g. `dynamic-assessment-pre_visit_check_in-info`. */
  testIDPrefix?: string;
};

/**
 * The static screen for a gate node flagged `infoScreen: true` — the ticket's
 * Pre-Visit Check-In intro is the first case, but this renders any gate
 * authored that way. Unlike `AssessmentContent`, there is no question to
 * answer here: the page's own title/description ARE the content, and a single
 * Continue is the only interaction. Deliberately its own component rather than
 * a branch inside `AssessmentContent` — that component's whole shape (the
 * per-key answer state, `useAssessmentQuestions`, choice/date/repeating-list
 * rendering) has nothing to do with a screen that has no question at all.
 */
export function InfoGateScreen({
  title,
  description,
  imageUrl,
  onContinue,
  isSubmitting = false,
  testIDPrefix,
}: InfoGateScreenProps) {
  return (
    <Screen
      busy={isSubmitting}
      footer={
        <CtaButton
          isBusy={isSubmitting}
          isDisabled={isSubmitting}
          onPress={onContinue}
          testID={testIDPrefix ? `${testIDPrefix}-continue` : undefined}
        >
          <T _str='Continue' />
        </CtaButton>
      }
    >
      <VStack space='2xl'>
        {imageUrl ? (
          <View className='w-full overflow-hidden rounded-vui-lg bg-background-50'>
            <Image
              source={{ uri: imageUrl }}
              resizeMode='cover'
              accessibilityLabel={title ?? ''}
              style={{
                width: '100%',
                aspectRatio: IMAGE_ASPECT_RATIO,
                maxHeight: IMAGE_MAX_HEIGHT,
              }}
              testID={testIDPrefix ? `${testIDPrefix}-image` : undefined}
            />
          </View>
        ) : null}
        <ScreenHeading title={title ?? ''} subtitle={description ?? undefined} />
      </VStack>
    </Screen>
  );
}
